import { call, put, takeLatest } from 'redux-saga/effects';
import * as actionTypes from './auth.actionTypes';
import * as authActions from './auth.actions';

import { registerUser, loginUser, verifyLoginTwoFactor } from './auth.helper';
import { setSession } from '../../../services/authService';
import { getLogoutSnapshot, clearLogoutSnapshot } from '../../../utils/userPrefs';
import { getSanitizedResponse } from './auth.utils';

import toast from 'react-hot-toast';

// Decide where to land after login. If this user has a logout snapshot from
// a prior session on this browser, restore it; otherwise default to /dashboard.
// We don't pre-validate /project/:id routes — if the project was deleted, the
// suppliers/survey-templates/files page handles 404 by redirecting to /dashboard.
const resolveLandingPath = (userId) => {
    if (!userId) return '/dashboard';
    const snap = getLogoutSnapshot(userId);
    clearLogoutSnapshot(userId);
    if (!snap || typeof snap.pathname !== 'string') return '/dashboard';
    // Never restore an auth route — users who logged out shouldn't bounce back to /session/login.
    if (snap.pathname.startsWith('/session/') || snap.pathname.startsWith('/auth/')) {
        return '/dashboard';
    }
    return `${snap.pathname}${snap.search || ''}`;
};

function* handleAuthSuccess(response, history, successAction) {
    // Pass the RAW response (not getSanitizedResponse's copy) — setSession needs the actual
    // access+refresh tokens to store in cookies. getSanitizedResponse strips tokens entirely
    // since that copy is what goes into Redux/localStorage, which should never hold them.
    yield call(setSession, response);
    // Only now flip isLoggedIn. EmailListener opens the socket when it flips and reads the token
    // from the cookie; dispatching first let setSession's "Welcome" toast re-render (and run that
    // effect) before the cookie existed, so the tab had no socket — no live updates, shown offline —
    // until a page reload.
    yield put(successAction);
    const landing = resolveLandingPath(response?.user?.id);
    yield call(history.push, landing);
}

function* registerUserSaga(action) {
    try {
        const { history, ...userDetails } = action.payload.userDetails;
        const response = yield call(registerUser, userDetails);
        yield put(authActions.registerUserSuccessAction(getSanitizedResponse(response)));
        // A new account starts as role 'guest' (zero permissions until the verification link is
        // clicked — see authService.verifyEmail) — landing straight on /dashboard like a normal
        // login would just hit 403s on every data fetch. Still store the (valid, if low-privilege)
        // session via setSession so redux/cookie state agree, but send them to log in again after
        // verifying rather than into a dashboard that can't load anything for them yet.
        yield call(setSession, response);
        toast.success("Account created! Check your email to verify your account, then log in.");
        yield call(history.push, '/session/login');
    } catch (e) {
        toast.error(e.message || 'Something went wrong!');
        yield put(authActions.registerUserFailureAction(e.message || 'Something went wrong!'));
    }
}

function* loginUserSaga(action) {
    try {
        const { rememberMe, history, ...loginCredentials } = action.payload.loginDetails;
        const response = yield call(loginUser, loginCredentials);
        // Strip password before persisting to redux (and via redux-persist, to localStorage).
        // Only the email is needed to pre-fill the form on next visit when rememberMe is set.
        const { password: _password, ...persistedCredentials } = loginCredentials;
        if (response.requiresTwoFactor) {
            // Password was correct but this device isn't (or is no longer) trusted — see backend
            // tokenService.TRUSTED_DEVICE_EXPIRATION_DAYS — a code was just emailed. Not a
            // failure: show the code-entry step rather than navigating anywhere.
            yield put(
                authActions.loginUserRequiresTwoFactorAction({
                    userId: response.userId,
                    rememberMe,
                    loginCredentials: persistedCredentials
                })
            );
            return;
        }
        yield call(
            handleAuthSuccess,
            response,
            history,
            authActions.loginUserSuccessAction({ rememberMe, loginCredentials: persistedCredentials, ...getSanitizedResponse(response) })
        );
    } catch (e) {
        toast.error(e.message || 'Something went wrong!');
        yield put(authActions.loginUserFailureAction(e.message || 'Something went wrong!'));
    }
}

function* verifyLoginTwoFactorSaga(action) {
    try {
        const { history, ...codePayload } = action.payload.details; // { userId, code }
        const response = yield call(verifyLoginTwoFactor, codePayload);
        yield call(handleAuthSuccess, response, history, authActions.verifyLoginTwoFactorSuccessAction(getSanitizedResponse(response)));
    } catch (e) {
        toast.error(e.message || 'Invalid or expired code');
        yield put(authActions.verifyLoginTwoFactorFailureAction(e.message || 'Invalid or expired code'));
    }
}

export default function* authSagas() {
    yield takeLatest(actionTypes.REGISTER_USER, registerUserSaga);
    yield takeLatest(actionTypes.LOGIN_USER, loginUserSaga);
    yield takeLatest(actionTypes.VERIFY_LOGIN_2FA, verifyLoginTwoFactorSaga);
}
